import { yupResolver } from "@hookform/resolvers/yup";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, Text } from "@pentabd/ui";

import { FORM_FIELDS } from "@constants/form";

import { InputTextRow } from "@components";
import { ParentType } from "@type/HRM/family";
import {
  ParentsFormDataType,
  parentsValidation,
} from "@validations/HRM/employee/familyInfoValidation";
import { useEffect, useState } from "react";

interface ParentPropsType {
  parentInfo: ParentType;
  changeParentInfo: (data: ParentType) => void;
  getFamilyInfoRequestOngoing: boolean;
  updateParentRequestOngoing: boolean;
  isProfileDisabled: boolean;
  canCreateEmployee: boolean;
  canEditEmployee: boolean;
}

const {
  FAMILY_INFO: { PARENT },
} = FORM_FIELDS.EMPLOYEE;

const ParentForm = ({ isProfileDisabled }: { isProfileDisabled: boolean }) => {
  const { t } = useTranslation();

  const {
    register,
    formState: { errors },
  } = useFormContext<ParentsFormDataType>();

  return (
    <div className="d-flex flex-column gap-8">
      <Text weight="semibold" color="title" size="lg">
        {t("EMPLOYEE.FAMILY_INFO.FORM.PARENT_INFO.HEADING")}
      </Text>

      <div className="d-flex flex-column gap-8">
        <InputTextRow
          label="EMPLOYEE.FAMILY_INFO.FORM.PARENT_INFO.FATHERS_NAME"
          register={{ ...register(PARENT.FATHER_NAME) }}
          error={errors}
          required
          disabled={isProfileDisabled}
        />
        <InputTextRow
          label="EMPLOYEE.FAMILY_INFO.FORM.PARENT_INFO.MOTHERS_NAME"
          register={{ ...register(PARENT.MOTHER_NAME) }}
          error={errors}
          required
          disabled={isProfileDisabled}
        />
      </div>
    </div>
  );
};

const Parent = ({
  parentInfo,
  changeParentInfo,
  getFamilyInfoRequestOngoing,
  updateParentRequestOngoing,
  isProfileDisabled,
  canCreateEmployee,
  canEditEmployee,
}: ParentPropsType) => {
  const { t } = useTranslation();
  const [disabledProfile, setDisabledProfile] = useState<boolean>(false);

  const methods = useForm<ParentType>({
    resolver: yupResolver(parentsValidation),
    values: parentInfo as ParentType,
  });

  const { handleSubmit } = methods;
  const onSubmit: SubmitHandler<ParentType> = (data: ParentType) => {
    changeParentInfo(data);
  };

  let isLoading = updateParentRequestOngoing || getFamilyInfoRequestOngoing;

  useEffect(() => {
    //for create
    if (
      parentInfo?.fathersId === null &&
      parentInfo?.mothersId === null &&
      canCreateEmployee
    ) {
      setDisabledProfile(true);
    }
    //for edit
    if (
      parentInfo?.fathersId !== null &&
      parentInfo?.mothersId !== null &&
      canEditEmployee
    ) {
      setDisabledProfile(true);
    }
  }, [parentInfo, canCreateEmployee, canEditEmployee]);

  return (
    <FormProvider {...methods}>
      <form className="container" onSubmit={handleSubmit(onSubmit)}>
        {/* This will contain only form fields. We seperate this because FormProvider should  cover the useFormContext*/}
        <ParentForm isProfileDisabled={isProfileDisabled || !disabledProfile} />

        <div className="row py-10">
          <div className="text-end col-12 col-lg-10">
            <Button
              loading={isLoading}
              htmlType="submit"
              fill="outline"
              type="primary"
              size="sm"
              disabled={isProfileDisabled || !disabledProfile}
            >
              {t("EMPLOYEE.FAMILY_INFO.FORM.SAVE_BUTTON")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Parent;
